import {
  makeStyles,
  Grid,
  Typography,
  MenuItem,
  IconButton,
} from "@material-ui/core";

import React, { useEffect, useState } from "react";
import colors from "constants/colors";
import clsx from "clsx";
import { routes } from "constants/routes";
import AmoPageHeader from "components/AmoPageHeader";
import { useGlobalStyles } from "hooks/globalStylesHook";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { useReconciliationChangeReviewId } from "./reconciliationReviewsHooks";
import AmoTextField from "components/inputs/AmoTextField";
import { formatToCurrency } from "utils/number";
import { reconciliationReasonChange } from "constants/reconciliationReasonChange";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import FileUploadList from "components/FileUploadList";
import { fileAssociations, fileTypes } from "constants/fileTypes";
import * as reviewService from "api/services/reviewsService";
import { snackbarTypes } from "constants/snackbar";
import { useIsMounted } from "hooks/useIsMounted";
import { useSnackbar } from "contexts/SnackbarContext";
import { ReactComponent as HelpIcon } from "img/icons/help_low_icon.svg";
import ReconciliationChangeEditHelpModal from "components/amo/ReconciliationChangeEditHelpModal";
import AmoFormActions from "components/AmoFormActions";

const useStyles = makeStyles((theme) => ({
  flexGrow: {
    flexGrow: 1,
  },
  title: {
    color: colors.black,
  },
  button: {
    minHeight: "3.125rem",
    fontSize: "1rem",
    borderWidth: "0.125rem",
    "&:hover": {
      borderWidth: "0.125rem",
    },
  },
  ellipsisIcon: {
    cursor: "pointer",
    fontSize: "3.2rem",
  },
  menuItemIcon: {
    color: "black",
    marginRight: "1.125rem",
  },
  minWidth: "initial",
  categoryIcon: {
    width: "100%",
    textAlign: "center",
  },
  dividerContainer: {
    paddingBottom: "0.688rem !important",
  },
  buttonsBar: {
    paddingBottom: "1.438rem !important",
  },
  itemSpacing: {
    marginBottom: theme.spacing(3),
  },
  boldLabel: {
    fontWeight: "bold",
  },
  // Убираем fullWidthElement, так как он мешает Grid
  iconContainer: {
    marginBottom: theme.spacing(2),
  },
  helpButtonWrapper: {
    display: "flex",
    justifyContent: "flex-end",
    width: "100%",
  },
}));

/**
 * The Questionnaire Review Edit Page.
 *
 * @returns {React.Component} Questionnaire Review Edit Page component.
 */
const ReconciliationReviewEditPage = () => {
  const globalClasses = useGlobalStyles();
  const history = useHistory();
  const location = useLocation();
  const classes = useStyles();
  const { reconciliationId } = useParams();
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [file, setFile] = useState(null);
  const [initialFile, setInitialFile] = useState(null);
  const [fileUploadAction, setFileUploadAction] = useState("Idle");

  const mounted = useIsMounted();
  const { showSnackbar } = useSnackbar();

  const reviseExpendituresReconciliationSchema = yup
    .object({
      authority: yup
        .string()
        .max(100)
        .required("Authority is a required field"),
      statusReconciliation: yup.string().required(),
    })
    .required();

  const defaultValues = {
    authority: "",
    statusReconciliation: `${reconciliationReasonChange.amoStaffReview}`,
    id: "",
  };

  const { data: reconciliation, isFetched } = useReconciliationChangeReviewId(
    reconciliationId
  );

  const { control, reset, formState, handleSubmit } = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(reviseExpendituresReconciliationSchema),
  });

  const { isValid, isSubmitting, isDirty } = formState;

  useEffect(() => {
    if (reconciliation && isFetched) {
      reset({
        authority: reconciliation.authority || "",
        statusReconciliation: reconciliation.statusReconciliation || "",
        id: reconciliation.id || "",
      });
      setFile(reconciliation?.file);
      setInitialFile(reconciliation?.file);
    }
  }, [reconciliation, isFetched, reset]);

  const saveReconciliation = async (formValues, fileId = null) => {
    const payload = {
      id: reconciliationId,
      year: reconciliation?.year,
      municipalityId: reconciliation?.municipalityId,
      projectId: reconciliation?.projectId,
      description: reconciliation?.reason,
      projectCategory: reconciliation?.projectCategory,
      amount: reconciliation?.amount,
      authority: formValues.authority,
      statusReconciliation: formValues.statusReconciliation,
    };
    try {
      if (file?.id !== initialFile?.id) {
        payload.fileId = fileId;
        await reviewService.putReconciliationChanges(payload);
      } else {
        payload.fileId = fileId;
        await reviewService.putReconciliationChanges(payload);
      }

      if (!mounted.current) {
        return;
      }

      showSnackbar("Reconciliation saved successfully", snackbarTypes.success);
      navigateBack();
    } catch (error) {
      console.error(error);

      if (!mounted.current) {
        return;
      }

      showSnackbar(
        "An error occurred while saving the reconciliation",
        snackbarTypes.error
      );
    }
  };

  const onClose = () => {
    reset(defaultValues);
    setFile(null);
    setInitialFile(null);
  };

  const onFileRollback = () => {
    setFileUploadAction("Idle");
    onClose();
  };

  const navigateBack = () =>
    history.push(`/reviews/reconciliations${location.search}`);

  const formActions = [
    {
      disabled: !isDirty || isSubmitting || !isValid,
      testId: "reconciliationEditSaveButton",
      label: "Save",
      onClick: handleSubmit((formValues) => saveReconciliation(formValues)),
    },
    {
      testId: "reconciliationEditCancelButton",
      label: "Cancel",
      color: "secondary",
      variant: "outlined",
      onClick: navigateBack,
    },
  ];

  return (
    <>
      <Grid container direction="column" spacing={0} wrap="nowrap">
        {/* Header */}
        <Grid item>
          <AmoPageHeader
            title="Reconciliation"
            subtitle="Edit a Change"
            backLinkText="Back to list"
            backLinkTo={routes.reviews.reconciliation.list}
          />
        </Grid>

        <Grid container className={classes.helpButtonWrapper}>
          <Grid item>
            <IconButton
              id="help-button"
              data-testid="helpButtonTest"
              aria-label="help"
              onClick={() => {
                setShowHelpModal(true);
              }}
            >
              <HelpIcon className={classes.icon} />
            </IconButton>
          </Grid>
        </Grid>

        {/* Body */}
        <Grid item xs className={globalClasses.editPageBodyContainer}>
          <Grid
            container
            direction="column"
            spacing={3}
            className={clsx(
              globalClasses.editPageBody22,
              globalClasses.editPageBodyText
            )}
            wrap="nowrap"
          >
            <Grid item>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography className={classes.boldLabel}>
                    Municipality
                  </Typography>
                  <Typography>{reconciliation?.municipalityName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <AmoTextField
                    label="Reason for change *"
                    control={control}
                    select
                    id="reconciliationStatus"
                    name="statusReconciliation"
                    fullWidth
                  >
                    <MenuItem
                      key="statusReconciliationNone"
                      value={`${reconciliationReasonChange.amoStaffReview}`}
                    >
                      {reconciliationReasonChange.amoStaffReview}
                    </MenuItem>
                    <MenuItem
                      key="statusReconciliationApproved"
                      value={`${reconciliationReasonChange.annualReportingCompleted}`}
                    >
                      {reconciliationReasonChange.annualReportingCompleted}
                    </MenuItem>
                    <MenuItem
                      key="statusReconciliationNotApproved"
                      value={`${reconciliationReasonChange.revisionRequested}`}
                    >
                      {reconciliationReasonChange.revisionRequested}
                    </MenuItem>
                  </AmoTextField>
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography className={classes.boldLabel}>
                    Description of change
                  </Typography>
                  <Typography>{reconciliation?.reason}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <AmoTextField
                    multiline
                    rows={1}
                    rowsMax={10}
                    control={control}
                    label="Authority *"
                    id="authority"
                    name="authority"
                    variant="outlined"
                    placeholder="Authority"
                    fullWidth
                    focused={false}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography className={classes.boldLabel}>Year</Typography>
                  <Typography>{reconciliation?.year}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <FileUploadList
                    key={file ? file.id : "empty-file"}
                    fileYear={reconciliation?.year}
                    fileAssociationId={fileAssociations.Reconciliation}
                    fileAssociationObjectId={reconciliation?.id}
                    municipalityId={reconciliation?.municipalityId}
                    showAllowedTypesHint
                    allowedTypes={[
                      fileTypes.docx,
                      fileTypes.msg,
                      fileTypes.pdf,
                      fileTypes.xlsx,
                    ]}
                    initialValue={file ? [file] : []}
                    onChange={(files) => {
                      setFile(files?.[0]);
                    }}
                    uploadAction={fileUploadAction}
                    onRollback={onFileRollback}
                  />
                </Grid>
              </Grid>
            </Grid>
            <Grid item>
              <Grid container spacing={3}>
                <Grid item xs={6}>
                  <Typography className={classes.boldLabel}>Amount</Typography>
                  <Typography>
                    {formatToCurrency(reconciliation?.amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        <AmoFormActions actions={formActions} hideLateralBorders />
      </Grid>
      <ReconciliationChangeEditHelpModal
        showModal={showHelpModal}
        closeModal={() => {
          setShowHelpModal(false);
        }}
      />
    </>
  );
};

export default ReconciliationReviewEditPage;
